<template>
  <div class="tab-container">
    <app-spinner v-if="!page_loaded" />
    <template v-if="page_loaded && table_climbing.items.length">
      <app-cells><h2 class="title">Climbing</h2></app-cells>
      <v-client-table
        :data="table_climbing.items"
        :columns="table_climbing.columns"
        :options="table_climbing.options"
        class="table-default table-default--climbing-groups-list"
      >
        <template #h__route.name>
          <div class="table-default__left">Route</div>
        </template>
        <template #h__name>
          <div class="table-default__left">Name</div>
        </template>
        <template #name="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'climbing-groups-detail-clients', params: { id: row.id } }"
              class="table-default__link-border"
            >
              {{ row.name }}
            </router-link>
          </div>
        </template>
        <template #tents="{ row }">
            <span
              v-if="!row.route.accommodation_type"
              class="status-tag"
              :style="{'backgroundColor': '#439ad1'}"
            >
              None
            </span>
          <span
            v-else
            class="status-tag"
            :style="{ 'backgroundColor': row.tents.clients_allocated === null ? '#e85c20' : row.tents.clients_allocated === row.pax ? '#77b017' : '#f0c427' }"
          >
              {{ row.tents.clients_allocated ? row.tents.clients_allocated : 0 }} / {{ row.pax }}
            </span>
        </template>
        <template #info="{ row }">
          <div class="table-default__controls">
              <span
                v-if="show_tabs"
                v-tooltip="{content: row.notes_climbing_manager, placement: 'top-start', offset: 10}"
                class="table-default__activity table-default__activity--notes"
                :class="{'table-default__activity--active': row.notes_climbing_manager}"
              />
            <a
              :href="`${row.gps?.shared_link}`"
              target="_blank"
              class="table-default__activity table-default__activity--gps"
              :class="{'table-default__activity--active': row.gps}"
            />
            <span
              v-if="show_tabs || is_operations_manager"
              v-tooltip="{content: `${row.chief_guide?.first_name} ${row.chief_guide?.last_name}`, placement: 'top-start', offset: 10}"
              class="table-default__activity table-default__activity--chief-guide"
              :class="{'table-default__activity--active': row.chief_guide}"
            />
            <span
              v-tooltip="{content: tooltipStartingPointContent(row), placement: 'top-start', offset: 10, html: true}"
              class="table-default__activity table-default__activity--starting"
              :class="{'table-default__activity--active': row.starting_point}"
            />
          </div>
        </template>
        <template #tour_start="{ row }">
          {{ dateFormat(row.tour_start) }}
        </template>
        <template #tour_end="{ row }">
          {{ dateFormat(row.tour_end) }}
        </template>
        <template #type="{ row }">
            <span class="status-tag" :style="{ 'backgroundColor': row.type.color || '#8a8a8a' }">
              {{ row.type ? row.type.name : '-' }}
            </span>
        </template>
        <template #route.name="{ row }">
          <div class="table-default__left">{{ row.route.name }} {{ row.route.duration }}</div>
        </template>
      </v-client-table>
    </template>
    <template v-if="page_loaded && table_safari.items.length">
      <app-cells><h2 class="title">Safari</h2></app-cells>
      <v-client-table
        :data="table_safari.items"
        :columns="table_safari.columns"
        :options="table_safari.options"
        class="table-default table-default--climbing-groups-list"
      >
        <template #h__name>
          <div class="table-default__left">Name</div>
        </template>
        <template #h__clients_itinerary>
          <div class="table-default__left">Itinerary</div>
        </template>
        <template #name="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'safari-groups-detail-clients', params: { id: row.id } }"
              class="table-default__link-border"
            >
              {{ row.name }}
            </router-link>
          </div>
        </template>
        <template #type="{ row }">
            <span class="status-tag" :style="{ 'backgroundColor': row.type.color || '#8a8a8a' }">
              {{ row.type ? row.type.name : '-' }}
            </span>
        </template>
        <template #clients_itinerary="{ row }">
          <div class="table-default__controls table-default__left">
              <span
                class="table-default__activity table-default__activity--clients table-default__activity--no-events"
                :class="{'table-default__activity--active': row.itinerary_description}"
              />
            <a
              :href="row.clients_itinerary"
              target="_blank"
              class="table-default__activity table-default__activity--itinerary"
              :class="{'table-default__activity--active': row.clients_itinerary}"
            />
            <button
              @click="onDownloadFile(row)"
              class="table-default__activity table-default__activity--driver"
              :class="{'table-default__activity--active': row.driver_itinerary && row.driver_itinerary.file}"
            ></button>
            <span v-if="row.short_itinerary">{{ row.short_itinerary }}</span>
          </div>
        </template>
        <template #number_of_clients="{ row }">
          <span
            v-tooltip="{ content: clientTableList(row.clients), placement: 'top-start', offset: 10, html: true }"
            :style="{cursor: 'pointer'}"
          >
            {{ row.number_of_clients }}
          </span>
        </template>
        <template #tour_start="{ row }">
          {{ dateFormat(row.tour_start) }}
        </template>
        <template #tour_end="{ row }">
          {{ dateFormat(row.tour_end) }}
        </template>
        <template #start_from="{ row }">
          {{ row.start_from?.short_name || '-' }}
        </template>
        <template #finish_at="{ row }">
          {{ row.finish_at?.short_name || '-' }}
        </template>
      </v-client-table>
    </template>
    <template v-if="page_loaded && table_trips.items.length">
      <app-cells><h2 class="title">Day trips</h2></app-cells>
      <v-client-table
        :data="table_trips.items"
        :columns="table_trips.columns"
        :options="table_trips.options"
        class="table-default table-default--climbing-groups-list"
      >
        <template #h__route.name>
          <div class="table-default__left">Route</div>
        </template>
        <template #h__name>
          <div class="table-default__left">Name</div>
        </template>
        <template #name="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'day-trips-groups-detail', params: { id: row.id } }"
              class="table-default__link-border"
            >
              {{ row.name }}
            </router-link>
          </div>
        </template>
        <template #number_of_clients="{ row }">
            <span
              v-tooltip="{ content: clientTableList(row.clients), placement: 'top-start', offset: 10, html: true }"
              :style="{cursor: 'pointer'}"
            >
              {{ row.clients.length }}
            </span>
        </template>
        <template #date="{ row }">
          {{ dateFormat(row.date) }}
        </template>
        <template #pick_up_time="{ row }">
          <template v-if="!row.pick_up_time_to_be_confirmed">{{ row.pick_up_time }}</template>
          <span
            v-else
            v-tooltip="{ content: 'To be confirmed', placement: 'top-start', offset: 10, container: false }"
            class="status-tag"
            style="background-color: #f0c427"
          >
              {{ row.pick_up_time }}
            </span>
        </template>
        <template #pick_up_location="{ row }">
            <span v-tooltip="{ content: row.pick_up_location?.name, placement: 'top-start', offset: 10, container: false }">
              {{ row.pick_up_location?.short_name || '-' }}
            </span>
        </template>
        <template #activity="{ row }">{{ row.activity?.short_name }}</template>
        <template #ready_to_join="{ row }">
            <span
              class="status-tag"
              :style="{ backgroundColor: row.ready_to_join ? '#77b017' : '#000' }"
            >
              {{ row.ready_to_join ? 'Yes': 'No' }}
            </span>
        </template>
        <template #created_by="{ row }">
          <div class="table-default__controls">
            <div class="table-default__photo">
              <img
                :src="getUserAvatar(row.created_by.crop.crop_url)" alt=""
                v-tooltip="{ content: row.created_by.full_name, placement: 'top-start', offset: 10 }"
              >
            </div>
          </div>
        </template>
      </v-client-table>
    </template>
    <template v-if="page_loaded && table_island.items.length">
      <app-cells><h2 class="title">Island vacation</h2></app-cells>
      <v-client-table
        :data="table_island.items"
        :columns="table_island.columns"
        :options="table_island.options"
        class="table-default table-default--climbing-groups-list"
      >
        <template #h__name>
          <div class="table-default__left">Name</div>
        </template>
        <template #name="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'island-vacation-groups-detail-clients', params: { id: row.id } }"
              class="table-default__link-border"
            >
              {{ row.name }}
            </router-link>
          </div>
        </template>
        <template #clients="{ row }">
            <span
              v-tooltip="{ content: clientTableList(row.clients), placement: 'top-start', offset: 10, html: true }"
              :style="{cursor: 'pointer'}"
            >
              {{ row.clients.length }}
            </span>
        </template>
        <template #tour_start="{ row }">
          {{ dateFormat(row.tour_start) }}
        </template>
        <template #tour_end="{ row }">
          {{ dateFormat(row.tour_end) }}
        </template>
      </v-client-table>
    </template>
    <template v-if="page_loaded && table_free_days.items.length">
      <app-cells><h2 class="title">Free days</h2></app-cells>
      <v-client-table
        :data="table_free_days.items"
        :columns="table_free_days.columns"
        :options="table_free_days.options"
        class="table-default"
      >
        <template #h__name><div class="table-default__left">Name</div></template>
        <template #name="{ row }">
          <div class="table-default__left">
            <router-link :to=" {name: 'free-days-detail', params: { id: row.id } }" class="table-default__link-border">
              {{ row.name }}
            </router-link>
          </div>
        </template>
        <template #clients="{ row }">
            <span
              v-tooltip="{ content: clientTableList(row.clients), placement: 'top-start', offset: 10, html: true }"
            >
              {{ row.clients.length }}
            </span>
        </template>
        <template #tour_start="{ row }">{{ dateFormat(row.tour_start) }} - {{ dateFormat(row.tour_end) }}</template>
        <template #location="{ row }">
            <span
              v-tooltip="{ content: row.location.name, placement: 'top-start', offset: 10 }"
            >
              {{ row.location.short_name }}
            </span>
        </template>
        <template #notes="{ row }">
          <div class="table-default__controls">
            <div
              v-tooltip="{ content: row.notes, placement: 'right', offset: 10 }"
              class="table-default__activity table-default__activity--notes"
              :class="{'table-default__activity--active': row.notes}"
            ></div>
          </div>
        </template>
      </v-client-table>
    </template>
  </div>
</template>

<script>
import {
  getClientsFreeDays,
  getClimbingGroups,
  getDayTripsDayTrips,
  getIslandVacationGroups,
  getSafariGroups,
} from '@/http'
import { format, parse } from 'date-fns'
import axios from 'axios'

export default {
  name: 'ClientsGroupsActivities',
  data() {
    return {
      page_loaded: false,
      table_climbing: {
        items: [],
        columns: ['name', 'pax', 'tents', 'info', 'type', 'route.name', 'tour_start', 'tour_end'],
        options: {
          headings: {
            'name': 'Name',
            'pax': 'Pax',
            'tents': 'Tents',
            'info': 'Info',
            'type': 'Type',
            'route.name': 'Route',
            'tour_start': 'Start',
            'tour_end': 'Finish',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_safari: {
        items: [],
        columns: ['name', 'number_of_clients', 'cars_required', 'type', 'clients_itinerary', 'tour_start', 'tour_end', 'duration', 'start_from', 'finish_at'],
        options: {
          headings: {
            'name': 'Name',
            'number_of_clients': 'Pax',
            'cars_required': 'Cars',
            'type': 'Type',
            'clients_itinerary': 'Itinerary',
            'tour_start': 'Start',
            'tour_end': 'Finish',
            'duration': 'Days',
            'start_from': 'Start from',
            'finish_at': 'Finish at',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_trips: {
        items: [],
        columns: ['name', 'number_of_clients', 'date', 'pick_up_time', 'pick_up_location', 'activity', 'ready_to_join', 'created_by'],
        options: {
          headings: {
            'name': 'Name',
            'number_of_clients': 'Pax',
            'date': 'Date',
            'pick_up_time': 'Pick-up time',
            'pick_up_location': 'Pick-up location',
            'activity': 'Activity',
            'ready_to_join': 'Join',
            'created_by': 'Manager',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_island: {
        items: [],
        columns: ['name', 'clients', 'tour_start', 'tour_end'],
        options: {
          headings: {
            'name': 'Name',
            'clients': 'Pax',
            'tour_start': 'Start',
            'tour_end': 'Finish',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_free_days: {
        items: [],
        columns: ['name', 'clients', 'tour_start', 'location', 'notes'],
        options: {
          headings: {
            id: 'Name',
            clients: 'Pax',
            tour_start: 'Dates',
            location: 'Location',
            notes: 'Notes',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
    }
  },
  computed: {
    show_tabs() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager') ||
        this.$store.state.userGroups.map(item => item.code).includes('climbing_manager')
    },
    is_operations_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_manager')
    },
  },
  created() {
    Promise.allSettled([
      getClimbingGroups({client_group: this.$route.params.id})
        .then(response => { this.table_climbing.items = response.data }),
      getSafariGroups({client_group: this.$route.params.id})
        .then(response => { this.table_safari.items = response.data }),
      getDayTripsDayTrips({client_group: this.$route.params.id})
        .then(response => { this.table_trips.items = response.data }),
      getIslandVacationGroups({client_group: this.$route.params.id})
        .then(response => { this.table_island.items = response.data }),
      getClientsFreeDays({client_group: this.$route.params.id})
        .then(response => { this.table_free_days.items = response.data })
    ]).then(() => { this.page_loaded = true })
  },
  methods: {
    clientTableList(clients) {
      let result = ''
      for (const key in clients) {
        result += `${clients[key].first_name} ${clients[key].last_name}<br>`
      }
      return result
    },
    tooltipStartingPointContent(data) {
      if (!data.starting_point) return ''
      let managers = ''
      data.departure_managers.forEach(item => {
        managers += `${item.full_name}, `
      })
      managers = managers.slice(0, -2)
      return `${data.starting_point.short_name}<br>Meeting time: ${data.meeting_time || '-'}<br>${managers}`
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    onDownloadFile(data) {
      this.file_loading = true
      axios.get(data.driver_itinerary.file, { responseType: 'blob' })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          const extension = data.driver_itinerary.file_name.split('.').pop()
          link.href = url
          link.setAttribute('download', `Driver itinerary ${data.name} ${this.dateFormat(data.tour_start)}-${this.dateFormat(data.tour_end)}.${extension}`)
          document.body.appendChild(link)
          link.click()
          URL.revokeObjectURL(url)
          this.file_loading = false
        })
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
  }
}
</script>
